import { api } from "@/api/client";
import type { PalletTemplate, SystemSettingsView, TemplateBinding } from "@/types/api";

export interface SystemSettingsUpdate {
  /** Склады возвратов: куда едет утиль и куда — переупаковка. Пара
   *  «ссылка + имя»: имя нужно, чтобы экран назвал склад и при недоступной 1С. */
  returns_utility_warehouse_ref?: string;
  returns_utility_warehouse_name?: string;
  returns_repack_warehouse_ref?: string;
  returns_repack_warehouse_name?: string;
  returns_fbs_warehouse_ref?: string;
  returns_fbs_warehouse_name?: string;
  honest_sign_base_url?: string | null;
  honest_sign_username?: string | null;
  honest_sign_password?: string | null;
  honest_sign_relay_required?: boolean;
  order_check_org_refs?: string[];
  transfer_check_org_refs?: string[];
  logistics_org_refs?: string[];
  receiving_org_refs?: string[];
  bitrix_enabled?: boolean;
  /** Пустая строка стирает вебхук, отсутствие поля — оставляет как есть. */
  bitrix_webhook?: string;
  bitrix_lab_users?: { id: number; name: string }[];
  /** Синхронизация остатков ФБС — см. `stock_sync/` на бэкенде. */
  stock_sync_enabled?: boolean;
  stock_sync_dry_run?: boolean;
  stock_sync_wb_enabled?: boolean;
  stock_sync_ozon_enabled?: boolean;
  stock_sync_interval_seconds?: number;
  stock_sync_max_delta?: number;
  bitrix_qc_users?: { id: number; name: string }[];
  bitrix_watchers?: { id: number; name: string }[];
  bitrix_deadline_hours?: number;
  bitrix_author_id?: number;
  bitrix_author_name?: string;
  inventory_org_refs?: string[];
  /** Юрлица, скрытые в «Сроках годности» дашборда; `[]` — никто. */
  dashboard_hidden_organization_refs?: string[];
  inventory_warehouse_refs?: string[];
  /** Юрлица и склады раздела «Перемещение»; `[]` — раздел не настроен. */
  stock_transfer_org_refs?: string[];
  stock_transfer_warehouse_refs?: string[];
  /** Склады с подтверждением приёмки: перемещение через приёмку, когда
   *  склад-получатель ИЛИ склад-отправитель в списке (в склад и из него);
   *  `[]` — выключено. */
  transfer_receipt_warehouse_refs?: string[];
  fulfilment_warehouse_refs?: string[];
  pallet_photo_required?: boolean;
  scan_method?: "tsd" | "camera" | "both";
  // ФБС: ссылки 1С и сетевой принтер этикеток.
  fbs_owner_org_ref?: string | null;
  fbs_owner_org_name?: string | null;
  fbs_owner_kontragent_ref?: string | null;
  // Имя храним рядом со ссылкой, чтобы страница настроек открывалась без
  // похода в 1С (и оставалась читаемой, когда 1С недоступна).
  fbs_owner_kontragent_name?: string | null;
  fbs_source_wh_ref?: string | null;
  fbs_source_wh_name?: string | null;
  fbs_department_ref?: string | null;
  fbs_department_name?: string | null;
  label_printer_ip?: string | null;
  label_printer_port?: number;
  /** Технические ссылки 1С: {колонка: {ref, name}}. Список колонок и подписи
   *  приходят с сервера (`SystemSettingsView.one_c_refs`). */
  one_c_refs?: Record<string, { ref: string | null; name: string | null }>;
}

export async function getSystemSettings(): Promise<SystemSettingsView> {
  const { data } = await api.get<SystemSettingsView>("/system-settings");
  return data;
}

export async function updateSystemSettings(
  body: SystemSettingsUpdate,
): Promise<SystemSettingsView> {
  const { data } = await api.put<SystemSettingsView>("/system-settings", body);
  return data;
}

// ── Привязки «шаблон паллетного листа → контрагент» ──────────────────────

export async function listTemplateBindings(): Promise<TemplateBinding[]> {
  const { data } = await api.get<TemplateBinding[]>("/system-settings/template-bindings");
  return data;
}

export async function addTemplateBinding(body: {
  template: PalletTemplate;
  kontragent_ref: string;
  kontragent_name: string;
}): Promise<TemplateBinding> {
  const { data } = await api.post<TemplateBinding>(
    "/system-settings/template-bindings",
    body,
  );
  return data;
}

export async function deleteTemplateBinding(id: number): Promise<void> {
  await api.delete(`/system-settings/template-bindings/${id}`);
}


/** Принтер одного раздела портала. Разделы могут указывать на один адрес —
 *  но настраиваются независимо: принтеры стоят в разных помещениях. */
export interface PrinterView {
  purpose: string;
  title: string;
  /** Куда печатать одной строкой: `tcp://host:port`, `usb:///dev/usb/lp0`,
   *  `serial://COM3`. Разбирает её relay-агент. */
  target: string | null;
  configured: boolean;
}

export async function listPrinters(): Promise<PrinterView[]> {
  const { data } = await api.get<PrinterView[]>("/system-settings/printers");
  return data;
}

export async function setPrinter(
  purpose: string,
  body: { target: string | null },
): Promise<PrinterView> {
  const { data } = await api.put<PrinterView>(`/system-settings/printers/${purpose}`, body);
  return data;
}

// ── Макеты этикеток ───────────────────────────────────────────────────────
//
// Макет живёт в БД, а не в коде: его верстают в GoLabel и меняют часто, а
// выкатывать релиз ради переноса поля на два миллиметра склад не должен.

export interface LabelTemplateView {
  code: string;
  name: string;
  template: string;
  /** Плейсхолдер → что он означает. Подсказка от бэка: имена полей в GoLabel
   *  по памяти не угадать. */
  placeholders: Record<string, string>;
}

export async function listLabelTemplates(): Promise<LabelTemplateView[]> {
  const { data } = await api.get<LabelTemplateView[]>("/system-settings/label-templates");
  return data;
}

export async function saveLabelTemplate(
  code: string,
  template: string,
): Promise<LabelTemplateView> {
  const { data } = await api.put<LabelTemplateView>(
    `/system-settings/label-templates/${code}`,
    { template },
  );
  return data;
}


export interface BitrixUser {
  id: number;
  name: string;
  position: string;
}

/** Сотрудники Битрикса для пикера. Числовые ID руками не вводят: ошибка в
 *  одной цифре ставит задачу чужому человеку, и заметят это, только когда
 *  товар пролежит непроверенным. */
export async function searchBitrixUsers(q?: string): Promise<BitrixUser[]> {
  const { data } = await api.get<BitrixUser[]>("/system-settings/bitrix-users", {
    params: { q: q || undefined },
  });
  return data;
}

/** Цель правила маршрута входного контроля (`receiving/constants.py`). */
export type InspectionRuleTarget = "lab" | "okk" | "skip";

/** Правило «группа номенклатуры 1С → куда». Имя и путь — кэш на момент
 *  сохранения (сервер перечитывает их из 1С при каждом PUT). */
export interface InspectionRoutingRule {
  group_ref: string;
  group_name: string;
  group_path: string;
  target: InspectionRuleTarget;
  updated_at: string | null;
}

export interface NomenclatureGroup {
  ref: string;
  name: string;
  /** «Материалы / Тара / Флаконы». */
  path: string;
}

const ROUTING = "/system-settings/inspection-routing";

/** Правила + предупреждение сервера (например, «Правил нет …»); `null` — всё в порядке. */
export interface InspectionRulesList {
  rules: InspectionRoutingRule[];
  warning: string | null;
}

export async function listInspectionRules(): Promise<InspectionRulesList> {
  const { data } = await api.get<InspectionRulesList>(`${ROUTING}/rules`);
  return data;
}

export async function saveInspectionRule(
  groupRef: string,
  target: InspectionRuleTarget,
): Promise<InspectionRoutingRule> {
  const { data } = await api.put<InspectionRoutingRule>(
    `${ROUTING}/rules/${groupRef}`,
    { target },
  );
  return data;
}

export async function deleteInspectionRule(groupRef: string): Promise<void> {
  await api.delete(`${ROUTING}/rules/${groupRef}`);
}

/** Группы (папки) номенклатуры 1С по подстроке имени, с путём; не больше 50. */
export async function searchNomenclatureGroups(q: string): Promise<NomenclatureGroup[]> {
  const { data } = await api.get<NomenclatureGroup[]>(`${ROUTING}/nomenclature-groups`, {
    params: { q: q || undefined },
  });
  return data;
}
